import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useLanguage } from "../i18n";
import { PAGE_CONTENT } from "../content-pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-warm px-4 py-3 text-sm font-semibold text-ink placeholder-mute/70 transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/25";

export default function EnquiryModal({ open, onOpenChange, subject }) {
  const { lang } = useLanguage();
  const e = PAGE_CONTENT[lang].enquiry;
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const set = (key) => (ev) => setForm((f) => ({ ...f, [key]: ev.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, subject: subject || e.title, locale: lang });
      toast.success(e.success);
      setForm({ name: "", email: "", message: "" });
      onOpenChange(false);
    } catch {
      toast.error(e.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="enquiry-modal" className="max-h-[90vh] overflow-y-auto rounded-[2rem] border-navy/10 bg-warm p-0 sm:max-w-lg">
        <div className="bg-navy px-8 py-8">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold text-white">{e.title}</DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-white/65">
              {subject ? `${subject} — ` : ""}
              {e.sub}
            </DialogDescription>
          </DialogHeader>
        </div>
        <form data-testid="enquiry-form" onSubmit={submit} className="grid gap-5 px-8 py-8">
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{e.name}</span>
            <input data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={e.namePh} className={inputCls} />
          </label>
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{e.email}</span>
            <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={e.emailPh} className={inputCls} />
          </label>
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{e.message}</span>
            <textarea data-testid="enquiry-message-input" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder={e.messagePh} className={`${inputCls} resize-none`} />
          </label>
          <button data-testid="enquiry-submit-button" type="submit" disabled={sending} className="btn-primary mt-1 w-full disabled:opacity-60">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {sending ? e.sending : e.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
